import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import bookingService from '../../services/bookingService';
import progressTrackingService from '../../services/progressTrackingService';
import { supabase } from '../../lib/supabaseClient';
import DatabaseService from '../../services/databaseService';
import StorageService from '../../services/storageService';
import toast from 'react-hot-toast';
import ProfileModal from '../layout/ProfileModal';
import {
  User,
  FileText,
  Calendar,
  Brain,
  Download,
  Heart,
  Target,
  MapPin,
  Clock,
  TrendingUp,
  Star,
  Book,
  Video,
  Phone,
  Mail,
  ChevronRight,
  Plus,
  Activity,
  LogOut,
  Eye,
  Home,
  Pill,
  Shield,
  Briefcase,
  Cake
} from 'lucide-react';

const SupervisorDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Get active tab from URL pathname
  // Example: /dashboard/profile -> activeTab = 'profile'
  // Example: /dashboard -> activeTab = 'profile' (default)
  const pathParts = location.pathname.split('/').filter(Boolean);
  const activeTab = pathParts.length > 1 ? pathParts[1] : 'profile';
  const [appointments, setAppointments] = useState([]);
  const [appointmentStats, setAppointmentStats] = useState({});
  const [progressData, setProgressData] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supervisorUid, setSupervisorUid] = useState(null);
  const [clinicalReport, setClinicalReport] = useState(null);
  const [showReportDetail, setShowReportDetail] = useState(false);
  const [supervisorReports, setSupervisorReports] = useState([]);
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [supervisorData, setSupervisorData] = useState({
    profile: {
      name: 'Loading...',
      email: 'Loading...',
      phone: 'Loading...',
      dateOfBirth: 'Loading...',
      address: 'Loading...',
      emergencyContact: 'Loading...'
    },
    clinic: {
      name: 'Loading...',
      address: 'Loading...',
      phone: 'Loading...',
      email: 'Loading...',
      doctorName: 'Loading...'
    },
    reports: [
      {
        id: 1,
        type: 'AIMS P&ID Report',
        date: '2024-09-15',
        status: 'Available',
        summary: 'Comprehensive brain activity analysis showing improved focus patterns'
      },
      {
        id: 2,
        type: 'Progress Assessment',
        date: '2024-08-20',
        status: 'Available',
        summary: 'Quarterly progress review with recommendations'
      }
    ],
    carePlans: [
      {
        id: 1,
        title: 'Focus Enhancement Program',
        progress: 75,
        nextSession: '2024-09-25',
        goals: ['Improve attention span', 'Reduce mental fatigue', 'Enhance cognitive flexibility']
      }
    ],
    resources: [
      {
        id: 1,
        title: 'Mindfulness Meditation Guide',
        type: 'video',
        duration: '15 min',
        unlocked: true
      },
      {
        id: 2,
        title: 'Brain Training Exercises',
        type: 'interactive',
        duration: '20 min',
        unlocked: true
      },
      {
        id: 3,
        title: 'Cognitive Health Assessment',
        type: 'assessment',
        duration: '30 min',
        unlocked: false
      }
    ]
  });

  const handleLogout = async () => {
    await logout();
  };

  // Fetch clinical report for the supervisor
  const fetchClinicalReport = async (supervisorId) => {
    try {
      console.log('INFO: Fetching clinical report for supervisor:', supervisorId);

      if (!supabase) {
        console.warn('WARNING: Supabase not available');
        return;
      }

      const { data: reports, error } = await supabase
        .from('clinical_reports')
        .select('*')
        .eq('supervisor_id', supervisorId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('ERROR: Failed to fetch clinical report:', error);
        toast.error('Failed to load clinical report');
        return;
      }

      if (reports && reports.length > 0) {
        console.log('SUCCESS: Clinical report found:', reports[0]);
        setClinicalReport(reports[0]);
      } else {
        console.log('INFO: No clinical report found for this supervisor');
        setClinicalReport(null);
      }
    } catch (error) {
      console.error('ERROR: Exception fetching clinical report:', error);
      toast.error('An error occurred while loading clinical report');
    }
  };

  // Fetch all reports for the supervisor (including response reports from super admin)
  const fetchSupervisorReports = async (supervisorId) => {
    try {
      console.log('INFO: Fetching reports for supervisor:', supervisorId);

      const reports = await DatabaseService.getReportsByPatient(supervisorId);
      console.log(`SUCCESS: Found ${reports.length} reports for supervisor`);

      setSupervisorReports(reports || []);
    } catch (error) {
      console.error('ERROR: Failed to fetch supervisor reports:', error);
      setSupervisorReports([]);
      toast.error('Failed to load supervisor reports');
    }
  };

  // Reload profile image from database
  const reloadProfileImage = async () => {
    try {
      console.log('=== RELOADING PROFILE IMAGE FROM DATABASE ===');
      console.log('INFO: User email:', user?.email);

      if (!user?.email) {
        console.warn('WARNING: No user email available to reload profile image');
        return;
      }

      const DatabaseService = (await import('../../services/databaseService')).default;

      // Find supervisor record by email
      const allSupervisors = await DatabaseService.get('supervisors');
      const userEmailLower = user.email.trim().toLowerCase();
      const supervisorRecord = allSupervisors.find(s => {
        if (!s.email) return false;
        return s.email.trim().toLowerCase() === userEmailLower;
      });

      if (supervisorRecord) {
        console.log('SUCCESS: Supervisor record found for reload');
        console.log('DEBUG: Profile image fields in database:');
        console.log('  - profile_image:', supervisorRecord.profile_image);
        console.log('  - avatar_url:', supervisorRecord.avatar_url);
        console.log('  - profileImage:', supervisorRecord.profileImage);
        console.log('  - avatar:', supervisorRecord.avatar);

        // Get profile image from database (priority order)
        const profileImage = supervisorRecord.profile_image || supervisorRecord.avatar_url ||
                           supervisorRecord.profileImage || supervisorRecord.avatar;

        if (profileImage) {
          console.log('SUCCESS: Profile image found in database:', profileImage);
          setProfileImageUrl(profileImage);
          console.log('SUCCESS: Profile image state updated');
        } else {
          console.warn('WARNING: No profile image found in database after reload');
        }
      } else {
        console.error('ERROR: Supervisor record not found for reload');
      }

      console.log('=== END PROFILE IMAGE RELOAD ===');
    } catch (error) {
      console.error('ERROR: Failed to reload profile image:', error);
    }
  };

  // Load real supervisor data from database
  useEffect(() => {
    const loadSupervisorData = async () => {
      if (!user?.id) {
        console.warn('WARNING: No user ID available');
        return;
      }

      try {
        setLoading(true);
        console.log('INFO: Loading supervisor data for user:', user);
        console.log('INFO: User ID:', user.id);
        console.log('INFO: User Email:', user.email);

        // Import DatabaseService
        const DatabaseService = (await import('../../services/databaseService')).default;

        // First, try to find supervisor by ID
        let supervisorRecord = await DatabaseService.findById('supervisors', user.id);
        console.log('INFO: Supervisor record from DB (by ID):', supervisorRecord);

        // If not found by ID, try to find by email
        if (!supervisorRecord && user.email) {
          console.log('WARNING: Supervisor not found by ID, searching by email...');
          console.log('DEBUG: User email to search:', user.email);
          console.log('DEBUG: User email (trimmed lowercase):', user.email.trim().toLowerCase());

          const allSupervisors = await DatabaseService.get('supervisors');
          console.log('INFO: Total supervisors in DB:', allSupervisors.length);
          console.log('INFO: All supervisors data:', allSupervisors);
          console.log('INFO: All supervisor emails:', allSupervisors.map(s => s.email));

          // More robust email matching with trim
          const userEmailLower = user.email.trim().toLowerCase();
          supervisorRecord = allSupervisors.find(s => {
            if (!s.email) return false;
            const supervisorEmailLower = s.email.trim().toLowerCase();
            console.log(`DEBUG: Comparing: "${supervisorEmailLower}" === "${userEmailLower}"`, supervisorEmailLower === userEmailLower);
            return supervisorEmailLower === userEmailLower;
          });

          console.log('INFO: Supervisor record from DB (by email):', supervisorRecord);
        }

        if (supervisorRecord) {
          console.log('=== SUPERVISOR RECORD LOADED ===');
          console.log('SUCCESS: Supervisor record found!');
          console.log('INFO: Supervisor ID:', supervisorRecord.id);
          console.log('INFO: Supervisor Email:', supervisorRecord.email);
          console.log('INFO: Supervisor record fields:', Object.keys(supervisorRecord));
          console.log('INFO: Full supervisor record:', supervisorRecord);

          // Store supervisor UID (external_id)
          if (supervisorRecord.external_id || supervisorRecord.externalId) {
            setSupervisorUid(supervisorRecord.external_id || supervisorRecord.externalId);
            console.log('INFO: Supervisor UID:', supervisorRecord.external_id || supervisorRecord.externalId);
          }

          // Debug: Log all possible avatar field values from both sources
          console.log('=== PROFILE IMAGE FETCH DEBUG ===');
          console.log('FROM USER OBJECT:');
          console.log('  - user.avatar:', user.avatar);
          console.log('  - user.profile_image:', user.profile_image);
          console.log('  - user.profileImage:', user.profileImage);
          console.log('  - user.avatar_url:', user.avatar_url);
          console.log('FROM DATABASE (supervisorRecord):');
          console.log('  - supervisorRecord.avatar:', supervisorRecord.avatar);
          console.log('  - supervisorRecord.profile_image:', supervisorRecord.profile_image);
          console.log('  - supervisorRecord.profileImage:', supervisorRecord.profileImage);
          console.log('  - supervisorRecord.avatar_url:', supervisorRecord.avatar_url);
          console.log('  - supervisorRecord.avatarUrl:', supervisorRecord.avatarUrl);

          // Load profile image from user/supervisor record (check all possible field names)
          // Priority: database first (supervisorRecord), then user object
          const profileImage = supervisorRecord.profile_image || supervisorRecord.avatar_url ||
                             supervisorRecord.profileImage || supervisorRecord.avatar ||
                             user.avatar || user.profile_image || user.profileImage || user.avatar_url;

          if (profileImage) {
            console.log('SUCCESS: Profile image URL found:', profileImage);
            console.log('SUCCESS: Setting profileImageUrl state to:', profileImage);
            setProfileImageUrl(profileImage);
          } else {
            console.warn('WARNING: No profile image found in any field!');
            console.log('INFO: Supervisor may not have uploaded a profile image yet');
          }
          console.log('=== END PROFILE IMAGE FETCH DEBUG ===');

          // Fetch clinical report for this supervisor
          await fetchClinicalReport(user.id);

          // Fetch all reports for this supervisor (including response reports)
          await fetchSupervisorReports(user.id);

          // Fetch project area data if supervisor has clinic_id or org_id
          let projectAreaData = null;
          const projectAreaId = supervisorRecord.clinicId || supervisorRecord.clinic_id || supervisorRecord.orgId || supervisorRecord.org_id || supervisorRecord.ownerId || supervisorRecord.owner_id;

          console.log('DEBUG: Looking for project area with ID:', projectAreaId);
          console.log('DEBUG: Supervisor record keys:', Object.keys(supervisorRecord));

          if (projectAreaId) {
            try {
              // Try new table name first, then fall back to old
              try {
                projectAreaData = await DatabaseService.findById('project_areas', projectAreaId);
              } catch {
                projectAreaData = await DatabaseService.findById('clinics', projectAreaId);
              }
              console.log('PROJECT_AREA: Project area data from DB:', projectAreaData);
            } catch (projectAreaError) {
              console.warn('WARNING: Failed to fetch project area data:', projectAreaError);
              toast.error('Failed to load project area information');
            }
          } else {
            console.warn('WARNING: No project area ID found in supervisor record');
          }

          // Update supervisor data state with real data from database
          const updatedData = {
            profile: {
              name: supervisorRecord.fullName || supervisorRecord.full_name || supervisorRecord.name || user.name || 'N/A',
              email: supervisorRecord.email || user.email || 'N/A',
              phone: supervisorRecord.phone || 'Not provided',
              dateOfBirth: supervisorRecord.dateOfBirth || supervisorRecord.date_of_birth || 'Not provided',
              address: supervisorRecord.address || 'Not provided',
              emergencyContact: supervisorRecord.emergencyContact || supervisorRecord.emergency_contact || 'Not provided'
            },
            clinic: projectAreaData ? {
              name: projectAreaData.name || 'N/A',
              address: projectAreaData.address || 'N/A',
              phone: projectAreaData.phone || 'N/A',
              email: projectAreaData.email || 'N/A',
              doctorName: projectAreaData.primaryDoctor || projectAreaData.primary_doctor || 'Not assigned'
            } : {
              name: 'No project area assigned',
              address: 'N/A',
              phone: 'N/A',
              email: 'N/A',
              doctorName: 'N/A'
            }
            // Note: Keep existing reports, carePlans, resources from initial state
          };

          console.log('INFO: Updated supervisor data:', updatedData);
          setSupervisorData(prevData => ({
            ...prevData,
            ...updatedData
          }));

          console.log('SUCCESS: Supervisor data loaded and updated successfully');
        } else {
          console.warn('WARNING: No supervisor record found for user ID:', user.id);
          console.log('DEBUG: Trying to find supervisor by email:', user.email);

          // Try to find by email as fallback
          if (user.email) {
            try {
              const supervisors = await DatabaseService.get('supervisors');
              const supervisorByEmail = supervisors.find(s => s.email === user.email);
              if (supervisorByEmail) {
                console.log('SUCCESS: Found supervisor by email:', supervisorByEmail);

                // Store supervisor UID
                if (supervisorByEmail.external_id || supervisorByEmail.externalId) {
                  setSupervisorUid(supervisorByEmail.external_id || supervisorByEmail.externalId);
                  console.log('INFO: Supervisor UID:', supervisorByEmail.external_id || supervisorByEmail.externalId);
                }

                // Fetch clinical report for this supervisor
                await fetchClinicalReport(user.id);

                // Fetch all reports for this supervisor (including response reports)
                await fetchSupervisorReports(user.id);

                // Retry with the found supervisor
                const projectAreaId = supervisorByEmail.clinicId || supervisorByEmail.clinic_id || supervisorByEmail.orgId || supervisorByEmail.org_id || supervisorByEmail.ownerId || supervisorByEmail.owner_id;
                let projectAreaData = null;
                if (projectAreaId) {
                  try {
                    projectAreaData = await DatabaseService.findById('project_areas', projectAreaId);
                  } catch {
                    projectAreaData = await DatabaseService.findById('clinics', projectAreaId);
                  }
                }
                setSupervisorData(prevData => ({
                  ...prevData,
                  profile: {
                    name: supervisorByEmail.fullName || supervisorByEmail.full_name || supervisorByEmail.name || user.name || 'N/A',
                    email: supervisorByEmail.email || user.email || 'N/A',
                    phone: supervisorByEmail.phone || 'Not provided',
                    dateOfBirth: supervisorByEmail.dateOfBirth || supervisorByEmail.date_of_birth || 'Not provided',
                    address: supervisorByEmail.address || 'Not provided',
                    emergencyContact: supervisorByEmail.emergencyContact || supervisorByEmail.emergency_contact || 'Not provided'
                  },
                  clinic: projectAreaData ? {
                    name: projectAreaData.name || 'N/A',
                    address: projectAreaData.address || 'N/A',
                    phone: projectAreaData.phone || 'N/A',
                    email: projectAreaData.email || 'N/A',
                    doctorName: projectAreaData.primaryDoctor || projectAreaData.primary_doctor || 'Not assigned'
                  } : prevData.clinic
                }));
              } else {
                console.warn('WARNING: Supervisor not found by email either, using user data as fallback');
                // Use user data as fallback
                setSupervisorData(prevData => ({
                  ...prevData,
                  profile: {
                    name: user.name || user.full_name || 'Supervisor',
                    email: user.email || 'Not provided',
                    phone: user.phone || 'Not provided',
                    dateOfBirth: user.dateOfBirth || user.date_of_birth || 'Not provided',
                    address: user.address || 'Not provided',
                    emergencyContact: user.emergencyContact || user.emergency_contact || 'Not provided'
                  },
                  clinic: {
                    name: 'No project area assigned',
                    address: 'N/A',
                    phone: 'N/A',
                    email: 'N/A',
                    doctorName: 'Not assigned'
                  }
                }));
              }
            } catch (emailError) {
              console.error('ERROR: Failed to find supervisor by email:', emailError);
              toast.error('Failed to load supervisor data');
              // Final fallback - use user object directly
              setSupervisorData(prevData => ({
                ...prevData,
                profile: {
                  name: user.name || user.full_name || 'Supervisor',
                  email: user.email || 'Not provided',
                  phone: user.phone || 'Not provided',
                  dateOfBirth: user.dateOfBirth || user.date_of_birth || 'Not provided',
                  address: user.address || 'Not provided',
                  emergencyContact: user.emergencyContact || user.emergency_contact || 'Not provided'
                },
                clinic: {
                  name: 'No project area assigned',
                  address: 'N/A',
                  phone: 'N/A',
                  email: 'N/A',
                  doctorName: 'Not assigned'
                }
              }));
            }
          } else {
            // No email to search with - use user data
            console.warn('WARNING: No email available for search, using user data');
            setSupervisorData(prevData => ({
              ...prevData,
              profile: {
                name: user.name || user.full_name || 'Supervisor',
                email: user.email || 'Not provided',
                phone: user.phone || 'Not provided',
                dateOfBirth: user.dateOfBirth || user.date_of_birth || 'Not provided',
                address: user.address || 'Not provided',
                emergencyContact: user.emergencyContact || user.emergency_contact || 'Not provided'
              },
              clinic: {
                name: 'No project area assigned',
                address: 'N/A',
                phone: 'N/A',
                email: 'N/A',
                doctorName: 'Not assigned'
              }
            }));
          }
        }

        // Disable appointments and progress tracking for now (tables not available)
        // These features can be enabled when the corresponding tables are created

      } catch (error) {
        console.error('ERROR: Failed to load supervisor data:', error);
        toast.error('Failed to load supervisor profile. Please try refreshing the page.');
        // Fallback on error - use user data
        setSupervisorData(prevData => ({
          ...prevData,
          profile: {
            name: user?.name || user?.full_name || 'Supervisor',
            email: user?.email || 'Not provided',
            phone: user?.phone || 'Not provided',
            dateOfBirth: user?.dateOfBirth || user?.date_of_birth || 'Not provided',
            address: user?.address || 'Not provided',
            emergencyContact: user?.emergencyContact || user?.emergency_contact || 'Not provided'
          },
          clinic: {
            name: 'No project area assigned',
            address: 'N/A',
            phone: 'N/A',
            email: 'N/A',
            doctorName: 'Not assigned'
          }
        }));
      } finally {
        setLoading(false);
      }
    };

    loadSupervisorData();
  }, [user?.id]);

  // Handle appointment booking
  const handleBookAppointment = async () => {
    try {
      // Simple booking - book next available follow-up slot
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dateStr = tomorrow.toISOString().split('T')[0];

      // Get available slots
      const availableSlots = await bookingService.getAvailableSlots(
        user?.clinic_id || 'default-clinic',
        dateStr,
        'follow-up'
      );

      if (availableSlots.length === 0) {
        alert('No available slots for tomorrow. Please try a different date.');
        return;
      }

      // Book the first available slot
      const firstSlot = availableSlots.find(slot => slot.available);
      if (!firstSlot) {
        alert('No available slots found. Please contact the clinic directly.');
        return;
      }

      const appointmentData = {
        patientId: user.id,
        clinicId: user?.clinic_id || 'default-clinic',
        appointmentType: 'follow-up',
        date: dateStr,
        time: firstSlot.time,
        requestedBy: user.id,
        notes: 'Requested via supervisor portal'
      };

      const newAppointment = await bookingService.bookAppointment(appointmentData);

      // Refresh appointments list
      const updatedAppointments = await bookingService.getPatientAppointments(user.id);
      setAppointments(updatedAppointments);

      alert(`Appointment booked successfully for ${dateStr} at ${firstSlot.display}!`);
      console.log('SUCCESS: Appointment booked:', newAppointment);

    } catch (error) {
      console.error('ERROR: Failed to book appointment:', error);
      alert(`Failed to book appointment: ${error.message}`);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile & History', icon: User },
    { id: 'reports', label: 'Reports & Plans', icon: FileText },
    { id: 'resources', label: 'Resources', icon: Book },
    { id: 'journey', label: 'Recurring Journey', icon: Calendar }
  ];

  // Helper functions for clinical report display
  const getAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const renderCheckboxList = (data, labelMap) => {
    if (!data) return <p className="text-gray-500 dark:text-gray-400 text-sm">No data available</p>;

    const items = Object.entries(data)
      .filter(([key, value]) => key !== 'other' && value === true)
      .map(([key]) => labelMap[key] || key);

    if (items.length === 0 && !data.other) {
      return <p className="text-gray-500 dark:text-gray-400 text-sm">None reported</p>;
    }

    return (
      <div className="space-y-1">
        {items.length > 0 && (
          <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300">
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        )}
        {data.other && (
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
            <span className="font-semibold">Other:</span> {data.other}
          </p>
        )}
      </div>
    );
  };

  // Label maps for clinical report data
  const presentingComplaintsLabels = {
    headaches: 'Headaches / Migraines',
    seizures: 'Seizures / Epileptic Episodes',
    dizziness: 'Dizziness / Balance Problems',
    attention: 'Attention / Concentration Difficulties',
    memory: 'Memory Issues',
    sleep: 'Sleep Disturbances',
    anxiety: 'Anxiety / Panic Symptoms',
    depression: 'Depression / Low Mood',
    irritability: 'Irritability / Emotional Dysregulation',
    fatigue: 'Fatigue / Low Energy'
  };

  const symptomDurationLabels = {
    sudden: 'Sudden Onset',
    gradual: 'Gradual Onset',
    acute: 'Acute (<1 month)',
    subacute: 'Subacute (1–6 months)',
    chronic: 'Chronic (>6 months)'
  };

  const pastMedicalHistoryLabels = {
    neurological: 'Neurological Disorders',
    psychiatric: 'Psychiatric Disorders',
    cardiovascular: 'Cardiovascular Conditions',
    endocrine: 'Endocrine/Metabolic',
    chronicPain: 'Chronic Pain / Fibromyalgia'
  };

  const medicationsLabels = {
    antidepressants: 'Antidepressants',
    anxiolytics: 'Anxiolytics / Benzodiazepines',
    antipsychotics: 'Antipsychotics',
    moodStabilizers: 'Mood Stabilizers',
    antiepileptics: 'Antiepileptics / Anticonvulsants',
    stimulants: 'Stimulants (ADHD medications)',
    sleepAids: 'Sleep Aids / Sedatives'
  };

  const familyHistoryLabels = {
    epilepsy: 'Epilepsy / Seizures',
    dementia: 'Dementia / Cognitive Decline',
    adhd: 'ADHD / Learning Disorders',
    moodDisorders: 'Mood Disorders',
    anxiety: 'Anxiety / OCD',
    substanceAbuse: 'Substance Abuse'
  };

  const ProfileSection = () => (
    <div className="space-y-6">
      {/* Personal Details */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header - Using Brand Color #323956 */}
        <div style={{ backgroundColor: '#323956' }} className="px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#E4EFFF' }}>
              <User className="h-6 w-6" style={{ color: '#323956' }} />
            </div>
            <h2 className="text-xl font-bold text-white">Personal Details</h2>
          </div>
        </div>

        <div className="p-6" style={{ backgroundColor: '#E4EFFF' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Full Name */}
            <div className="group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <User className="h-3.5 w-3.5" />
                <span>Full Name</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <p className="font-medium" style={{ color: '#323956' }}>{supervisorData.profile.name}</p>
              </div>
            </div>

            {/* Email */}
            <div className="group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <Mail className="h-3.5 w-3.5" />
                <span>Email</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <p className="font-medium" style={{ color: '#323956' }}>{supervisorData.profile.email}</p>
              </div>
            </div>

            {/* Phone */}
            <div className="group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <Phone className="h-3.5 w-3.5" />
                <span>Phone</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <p className="font-medium" style={{ color: '#323956' }}>{supervisorData.profile.phone}</p>
              </div>
            </div>

            {/* Date of Birth */}
            <div className="group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <Cake className="h-3.5 w-3.5" />
                <span>Date of Birth</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <p className="font-medium" style={{ color: '#323956' }}>{supervisorData.profile.dateOfBirth}</p>
              </div>
            </div>

            {/* Address */}
            <div className="md:col-span-2 group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <MapPin className="h-3.5 w-3.5" />
                <span>Address</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <p className="font-medium" style={{ color: '#323956' }}>{supervisorData.profile.address}</p>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="md:col-span-2 group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <Shield className="h-3.5 w-3.5" />
                <span>Emergency Contact</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <p className="font-medium" style={{ color: '#323956' }}>{supervisorData.profile.emergencyContact}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clinic Information */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header - Using Brand Color #323956 */}
        <div style={{ backgroundColor: '#323956' }} className="px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#CAE0FF' }}>
              <Heart className="h-6 w-6" style={{ color: '#323956' }} />
            </div>
            <h2 className="text-xl font-bold text-white">Your Project Area</h2>
          </div>
        </div>

        <div className="p-6" style={{ backgroundColor: '#CAE0FF' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Project Area Name */}
            <div className="group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <Briefcase className="h-3.5 w-3.5" />
                <span>Project Area Name</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <p className="font-semibold" style={{ color: '#323956' }}>{supervisorData.clinic.name}</p>
              </div>
            </div>

            {/* Primary Contact */}
            <div className="group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <User className="h-3.5 w-3.5" />
                <span>Primary Contact</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <p className="font-semibold" style={{ color: '#323956' }}>{supervisorData.clinic.doctorName}</p>
              </div>
            </div>

            {/* Phone */}
            <div className="group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <Phone className="h-3.5 w-3.5" />
                <span>Phone</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <a href={`tel:${supervisorData.clinic.phone}`} className="font-medium flex items-center space-x-2 hover:opacity-80" style={{ color: '#323956' }}>
                  <span>{supervisorData.clinic.phone}</span>
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <Mail className="h-3.5 w-3.5" />
                <span>Email</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <a href={`mailto:${supervisorData.clinic.email}`} className="font-medium flex items-center space-x-2 hover:opacity-80" style={{ color: '#323956' }}>
                  <span>{supervisorData.clinic.email}</span>
                </a>
              </div>
            </div>

            {/* Address */}
            <div className="md:col-span-2 group">
              <label className="flex items-center space-x-2 text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#323956' }}>
                <MapPin className="h-3.5 w-3.5" />
                <span>Address</span>
              </label>
              <div className="bg-white border border-gray-300 p-3.5 rounded-lg hover:border-[#323956] transition-colors">
                <p className="font-medium" style={{ color: '#323956' }}>{supervisorData.clinic.address}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clinical Report Information */}
      {clinicalReport && (
        <>
          {/* Supervisor Information from Clinical Report */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                <User className="h-6 w-6 text-blue-900 dark:text-blue-100" />
              </div>
              <h2 className="text-xl font-semibold text-blue-900 dark:text-blue-100">Clinical Report Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Full Name</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-3 rounded-lg">{clinicalReport.full_name || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Date of Birth</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-3 rounded-lg">
                  {formatDate(clinicalReport.date_of_birth)} (Age: {getAge(clinicalReport.date_of_birth)} years)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Gender</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-3 rounded-lg">{clinicalReport.gender || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Handedness</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-3 rounded-lg">{clinicalReport.handedness || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Occupation</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-3 rounded-lg">{clinicalReport.occupation || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Supervisor ID</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-3 rounded-lg">{clinicalReport.supervisor_uid || supervisorUid || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Date of Test</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-3 rounded-lg">{formatDate(clinicalReport.date_of_test)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Referring Physician</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-3 rounded-lg">{clinicalReport.referring_physician || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Reason for Referral</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-3 rounded-lg">{clinicalReport.referral_reason || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Clinical & Medical History */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
                <Activity className="h-6 w-6 text-green-900 dark:text-green-100" />
              </div>
              <h2 className="text-xl font-semibold text-green-900 dark:text-green-100">Clinical & Medical History</h2>
            </div>

            <div className="space-y-4">
              <div>
                <p className="font-semibold text-gray-900 dark:text-white mb-2">Presenting Complaints:</p>
                {renderCheckboxList(clinicalReport.presenting_complaints, presentingComplaintsLabels)}
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white mb-2">Duration & Onset of Symptoms:</p>
                {renderCheckboxList(clinicalReport.symptom_duration, symptomDurationLabels)}
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white mb-2">Past Medical History:</p>
                {renderCheckboxList(clinicalReport.past_medical_history, pastMedicalHistoryLabels)}
              </div>
            </div>
          </div>

          {/* Medication History */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                <Heart className="h-6 w-6 text-purple-900 dark:text-purple-100" />
              </div>
              <h2 className="text-xl font-semibold text-purple-900 dark:text-purple-100">Medication History</h2>
            </div>

            <div className="space-y-3">
              {renderCheckboxList(clinicalReport.medications, medicationsLabels)}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const ReportsSection = () => {
    // Define 7 brain health parameters
    const brainParameters = [
      {
        id: 1,
        name: 'Cognition',
        value: '85.50',
        unit: 'Score',
        status: 'normal',
        range: 'Normal Range',
        description: 'Cognition refers to mental processes like thinking, memory, and problem-solving. Good cognitive health supports daily functioning and quality of life.',
        normal: 4,
        borderline: 1,
        abnormal: 2
      },
      {
        id: 2,
        name: 'Stress',
        value: '120.00',
        unit: 'Score',
        status: 'high',
        range: 'Higher Than Normal',
        description: 'Stress is the body\'s response to challenges. Chronic stress can affect mental and physical health, impacting mood, sleep, and overall well-being.',
        normal: 3,
        borderline: 2,
        abnormal: 3
      },
      {
        id: 3,
        name: 'Focus + Attention',
        value: '78.30',
        unit: 'Score',
        status: 'normal',
        range: 'Normal Range',
        description: 'Focus and attention involve the ability to concentrate on tasks. Strong attention skills support productivity, learning, and daily activities.',
        normal: 5,
        borderline: 1,
        abnormal: 1
      },
      {
        id: 4,
        name: 'Burnout & Fatigue',
        value: '95.20',
        unit: 'Score',
        status: 'borderline',
        range: 'Borderline',
        description: 'Burnout and fatigue result from prolonged stress or overwork. Managing these is essential for maintaining energy levels and preventing exhaustion.',
        normal: 2,
        borderline: 3,
        abnormal: 2
      },
      {
        id: 5,
        name: 'Emotional Regulation',
        value: '72.10',
        unit: 'Score',
        status: 'normal',
        range: 'Normal Range',
        description: 'Emotional regulation is the ability to manage and respond to emotions effectively. Good emotional regulation supports mental health and relationships.',
        normal: 6,
        borderline: 0,
        abnormal: 1
      },
      {
        id: 6,
        name: '[+ Under 18] Learning',
        value: '88.90',
        unit: 'Score',
        status: 'normal',
        range: 'Normal Range',
        description: 'Learning capacity is crucial for development in young individuals. It involves acquiring knowledge, skills, and adapting to new information.',
        normal: 5,
        borderline: 1,
        abnormal: 1
      },
      {
        id: 7,
        name: 'Creativity',
        value: '81.40',
        unit: 'Score',
        status: 'normal',
        range: 'Normal Range',
        description: 'Creativity involves generating new ideas and solutions. It supports innovation, problem-solving, and personal expression.',
        normal: 4,
        borderline: 2,
        abnormal: 1
      }
    ];

    return (
      <div className="space-y-6">
        {/* AIMS Reports */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-[#E4EFFF] dark:bg-blue-900/30 rounded-lg">
              <Brain className="h-6 w-6 text-[#323956] dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">AIMS Reports</h2>
          </div>

        <div className="grid gap-4">
          {clinicalReport ? (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Test Card Header */}
              <div className="flex items-center p-5 bg-white dark:bg-gray-800">
                {/* Progress circle */}
                <div className="mr-4">
                  <div className="w-16 h-16 rounded-full border-4 border-green-500 flex items-center justify-center">
                    <svg className="w-16 h-16 transform -rotate-90">
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="#e5e7eb"
                        strokeWidth="4"
                        fill="none"
                      />
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="#22c55e"
                        strokeWidth="4"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - 0.85)}`}
                      />
                    </svg>
                  </div>
                </div>

                {/* Test details */}
                <div className="flex-1">
                  <div className="mb-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Test Name</p>
                    <h3 className="font-semibold text-blue-600 dark:text-blue-400">
                      Clinical AIMS P&ID Report
                    </h3>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Collected</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {new Date(clinicalReport.created_at || clinicalReport.date_of_test).toLocaleDateString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col space-y-2 ml-4">
                  <button
                    onClick={async () => {
                      try {
                        // Check if clinical report has an associated file
                        if (clinicalReport.file_path || clinicalReport.filePath || clinicalReport.report_file || clinicalReport.fileName || clinicalReport.file_name) {
                          const filePath = clinicalReport.file_path || clinicalReport.filePath || clinicalReport.report_file;

                          // Get original filename with extension
                          let fileName = clinicalReport.fileName || clinicalReport.file_name || 'Clinical_AIMS_PID_Report';

                          // If filePath exists but no fileName, extract from path
                          if (filePath && !fileName.includes('.')) {
                            const pathParts = filePath.split('/');
                            const fileNameFromPath = pathParts[pathParts.length - 1];
                            if (fileNameFromPath) {
                              fileName = fileNameFromPath;
                            }
                          }

                          // Ensure fileName has an extension
                          if (!fileName.includes('.')) {
                            fileName = fileName + '.pdf'; // Default to PDF if no extension
                          }

                          // Get signed URL from Supabase Storage
                          const downloadUrl = await StorageService.getSignedUrl(filePath, 300);

                          if (downloadUrl) {
                            // Fetch the file as blob to force download
                            const response = await fetch(downloadUrl);
                            const blob = await response.blob();

                            // Create blob URL
                            const blobUrl = window.URL.createObjectURL(blob);

                            // Create download link
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = fileName;
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();

                            // Cleanup
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(blobUrl);

                            toast.success(`Downloading ${fileName}`);
                          } else {
                            toast.error('Could not generate download link');
                          }
                        } else {
                          // No file available - this is just database data
                          toast.info('This report is displayed as parameters only. No file available for download.');
                        }
                      } catch (error) {
                        console.error('Download error:', error);
                        toast.error(`Download failed: ${error.message}`);
                      }
                    }}
                    className="bg-teal-700 hover:bg-teal-800 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </button>
                  <button
                    onClick={() => setShowReportDetail(!showReportDetail)}
                    className="bg-gray-700 dark:bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View Detail</span>
                  </button>
                </div>
              </div>

              {/* Expandable Test Details Section - All Parameters */}
              {showReportDetail && (
                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-6 space-y-4">
                  {/* Map through all 7 brain parameters */}
                  {brainParameters.map((parameter) => (
                    <div key={parameter.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
                      {/* Combined Header Row with Table Headers and Filters */}
                      <div className="flex items-center justify-between pb-3 border-b border-gray-300 dark:border-gray-600">
                        {/* Table Headers */}
                        <div className="grid grid-cols-3 gap-8 flex-1">
                          <div>
                            <span className="text-sm font-semibold" style={{ color: '#323956' }}>Parameter Name</span>
                          </div>
                          <div>
                            <span className="text-sm font-semibold" style={{ color: '#323956' }}>Result</span>
                          </div>
                          <div>
                            <span className="text-sm font-semibold" style={{ color: '#323956' }}>Range</span>
                          </div>
                        </div>

                        {/* Filter Checkboxes */}
                        <div className="flex items-center space-x-3 ml-6">
                          <label className="flex items-center space-x-1.5 text-xs" style={{ color: '#DC2626' }}>
                            <input type="checkbox" className="rounded w-3 h-3" />
                            <span>Less than 0.00</span>
                          </label>
                          <label className="flex items-center space-x-1.5 text-xs" style={{ color: '#F59E0B' }}>
                            <input type="checkbox" className="rounded w-3 h-3" />
                            <span>Between 0.00 to 6.45</span>
                          </label>
                          <label className="flex items-center space-x-1.5 text-xs" style={{ color: '#14B8A6' }}>
                            <input type="checkbox" className="rounded w-3 h-3" />
                            <span>Between 6.45 to 123.55</span>
                          </label>
                          <label className="flex items-center space-x-1.5 text-xs" style={{ color: '#DC2626' }}>
                            <input type="checkbox" className="rounded w-3 h-3" />
                            <span>Greater than 125.00</span>
                          </label>

                          {/* Result Button */}
                          <button className="px-5 py-1.5 text-white text-sm rounded-md hover:opacity-90 transition-colors ml-2" style={{ backgroundColor: '#323956' }}>
                            Result
                          </button>
                        </div>
                      </div>

                      {/* Parameter Data Row */}
                      <div className="grid grid-cols-3 gap-8">
                        <div>
                          <span className="font-semibold" style={{ color: '#323956' }}>{parameter.name}</span>
                        </div>
                        <div>
                          <span className={`font-bold ${
                            parameter.status === 'high' ? 'text-red-600' :
                            parameter.status === 'borderline' ? 'text-orange-500' :
                            'text-green-600'
                          }`}>
                            {parameter.value} {parameter.unit}
                          </span>
                        </div>
                        <div>
                          <span className={`text-sm font-medium ${
                            parameter.status === 'normal' ? 'text-green-600' :
                            parameter.status === 'borderline' ? 'text-orange-500' :
                            'text-red-600'
                          }`}>
                            {parameter.range}
                          </span>
                        </div>
                      </div>

                      {/* Visual Progress Bar */}
                      <div className="pt-2">
                        <div className="flex items-center justify-end mb-2">
                          <span className="text-sm font-bold" style={{ color: '#323956' }}>{parameter.value}</span>
                        </div>
                        <div className="relative h-3 bg-gradient-to-r from-green-400 via-yellow-400 via-orange-400 to-red-500 rounded-full shadow-inner"></div>
                      </div>

                      {/* Educational Information */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 border-l-4 p-4 rounded text-xs leading-relaxed" style={{ borderColor: '#323956', color: '#323956' }}>
                        <p>
                          <strong>{parameter.name}:</strong> {parameter.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {/* Uploaded Response Reports */}
          {supervisorReports.length > 0 && supervisorReports.map((report) => (
            <div key={report.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Report Card Header */}
              <div className="flex items-center p-5 bg-white dark:bg-gray-800">
                {/* File Icon */}
                <div className="mr-4">
                  <div className="w-16 h-16 rounded-lg bg-[#E4EFFF] dark:bg-blue-900/30 flex items-center justify-center">
                    <FileText className="h-8 w-8 text-[#323956] dark:text-blue-400" />
                  </div>
                </div>

                {/* Report details */}
                <div className="flex-1">
                  <div className="mb-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">File Name</p>
                    <h3 className="font-semibold text-blue-600 dark:text-blue-400">
                      {report.fileName || report.file_name || 'Report'}
                    </h3>
                    {report.reportData?.isResponseReport && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 mt-1">
                        Response Report
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Collected</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {new Date(report.createdAt || report.created_at).toLocaleDateString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col space-y-2 ml-4">
                  <button
                    onClick={async () => {
                      try {
                        const filePath = report.filePath || report.file_path || report.storagePath || report.reportData?.filePath;

                        if (!filePath) {
                          toast.error('File path not found');
                          return;
                        }

                        // Get original filename with extension
                        let fileName = report.fileName || report.file_name || report.reportData?.fileName;

                        // If no fileName, extract from filePath
                        if (!fileName && filePath) {
                          const pathParts = filePath.split('/');
                          fileName = pathParts[pathParts.length - 1];
                        }

                        // Fallback to generic name with extension if still not found
                        if (!fileName) {
                          fileName = 'report.pdf';
                        }

                        // Get signed URL from Supabase Storage
                        const downloadUrl = await StorageService.getSignedUrl(filePath, 300);

                        if (downloadUrl) {
                          // Fetch the file as blob to force download
                          const response = await fetch(downloadUrl);
                          const blob = await response.blob();

                          // Create blob URL
                          const blobUrl = window.URL.createObjectURL(blob);

                          // Create download link
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = fileName;
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();

                          // Cleanup
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(blobUrl);

                          toast.success(`Downloading ${fileName}`);
                        } else {
                          toast.error('Could not generate download link');
                        }
                      } catch (error) {
                        console.error('Download error:', error);
                        toast.error(`Download failed: ${error.message}`);
                      }
                    }}
                    className="bg-teal-700 hover:bg-teal-800 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </button>
                  <button
                    onClick={() => {
                      // Toggle inline expansion
                      if (expandedReportId === report.id) {
                        setExpandedReportId(null);
                      } else {
                        setExpandedReportId(report.id);
                      }
                    }}
                    className="bg-gray-700 dark:bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View Detail</span>
                  </button>
                </div>
              </div>

              {/* Expandable Test Details Section - All Parameters */}
              {expandedReportId === report.id && (
                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-6 space-y-4">
                  {/* Map through all 7 brain parameters */}
                  {brainParameters.map((parameter) => (
                    <div key={parameter.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
                      {/* Table Structure with Headers, Data, and Filters */}
                      <div className="space-y-3">
                        {/* Row 1: Headers + Checkboxes */}
                        <div className="flex items-center justify-between">
                          {/* Left: Table Headers (3 columns) */}
                          <div className="flex items-center space-x-16 flex-1">
                            <div className="w-40">
                              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Parameter<br/>Name</span>
                            </div>
                            <div className="w-40">
                              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Result</span>
                            </div>
                            <div className="w-40">
                              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Range</span>
                            </div>
                          </div>

                          {/* Right: Filter Checkboxes + Result Button */}
                          <div className="flex items-center space-x-3">
                            <label className="flex items-center space-x-1.5 text-xs" style={{ color: '#DC2626' }}>
                              <input type="checkbox" className="rounded w-3 h-3" />
                              <span>Less than 0.00</span>
                            </label>
                            <label className="flex items-center space-x-1.5 text-xs" style={{ color: '#F59E0B' }}>
                              <input type="checkbox" className="rounded w-3 h-3" />
                              <span>Between 0.00 to 6.45</span>
                            </label>
                            <label className="flex items-center space-x-1.5 text-xs" style={{ color: '#14B8A6' }}>
                              <input type="checkbox" className="rounded w-3 h-3" />
                              <span>Between 6.45 to 123.55</span>
                            </label>
                            <label className="flex items-center space-x-1.5 text-xs" style={{ color: '#DC2626' }}>
                              <input type="checkbox" className="rounded w-3 h-3" />
                              <span>Greater than 125.00</span>
                            </label>
                            <button className="px-5 py-1.5 text-white text-sm rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: '#323956' }}>
                              Result
                            </button>
                          </div>
                        </div>

                        {/* Row 2: Data Values (3 columns aligned with headers) */}
                        <div className="flex items-center space-x-16 pb-3 border-b border-gray-300 dark:border-gray-600">
                          <div className="w-40">
                            <p className="font-semibold text-gray-900 dark:text-white text-base">{parameter.name}</p>
                          </div>
                          <div className="w-40">
                            <p className="font-semibold text-gray-900 dark:text-white text-base">
                              {parameter.value} {parameter.unit}
                            </p>
                          </div>
                          <div className="w-40">
                            <p className="text-gray-900 dark:text-white text-base">{parameter.range}</p>
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar with Multi-Color Gradient */}
                      <div className="w-full">
                        <div className="flex justify-end mb-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{parameter.value}</span>
                        </div>
                        <div className="relative w-full h-3 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to right, #10B981 0%, #10B981 33%, #F59E0B 33%, #F59E0B 66%, #DC2626 66%, #DC2626 100%)' }}>
                          {/* Value Marker */}
                          <div
                            className="absolute top-0 bottom-0 w-1 bg-gray-800 dark:bg-white shadow-lg"
                            style={{ left: `${Math.min((parseFloat(parameter.value) / 150) * 100, 100)}%`, transform: 'translateX(-50%)' }}
                          >
                            {/* Diamond marker at top */}
                            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-gray-800 dark:bg-white rotate-45"></div>
                          </div>
                        </div>
                      </div>

                      {/* Parameter Description */}
                      <div className="mt-3 p-3 rounded-lg border-l-4" style={{ backgroundColor: '#FEF3C7', borderColor: '#F59E0B' }}>
                        <p className="text-sm text-gray-800">
                          <span className="font-semibold text-gray-900">{parameter.name}:</span> {parameter.description}
                        </p>
                      </div>

                      {/* Analysis Summary */}
                      <div className="grid grid-cols-3 gap-4 mt-3">
                        <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{parameter.normal}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Normal</p>
                        </div>
                        <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <p className="text-2xl font-bold text-orange-500 dark:text-orange-400">{parameter.borderline}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Borderline</p>
                        </div>
                        <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{parameter.abnormal}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Abnormal</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* No Reports Message */}
          {!clinicalReport && supervisorReports.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-medium">No Reports Available</p>
              <p className="text-sm mt-2">Your reports will appear here once they are generated by your project area.</p>
            </div>
          )}
        </div>
      </div>

      {/* Personalized Care Plans */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-[#E4EFFF] dark:bg-blue-900/30 rounded-lg">
            <Target className="h-6 w-6 text-[#323956] dark:text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Personalized Care Plans</h2>
        </div>

        <div className="space-y-4">
          {supervisorData.carePlans && Array.isArray(supervisorData.carePlans) && supervisorData.carePlans.map((plan) => (
            <div key={plan.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">{plan.title}</h3>
                <span className="text-sm text-[#323956] dark:text-blue-400 font-medium">{plan.progress}% Complete</span>
              </div>

              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                <div
                  className="bg-[#323956] dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${plan.progress}%` }}
                ></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Next Session</label>
                  <p className="text-gray-900 dark:text-white">{plan.nextSession}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Goals</label>
                <ul className="space-y-1">
                  {plan.goals.map((goal, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                      <Star className="h-4 w-4 text-[#F5D05D] dark:text-yellow-400" />
                      <span>{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
  };

  const ResourcesSection = () => (
    <div className="space-y-6">
      {/* Online Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-[#E4EFFF] dark:bg-blue-900/30 rounded-lg">
            <Book className="h-6 w-6 text-[#323956] dark:text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Unlocked Content</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {supervisorData.resources && Array.isArray(supervisorData.resources) && supervisorData.resources.map((resource) => (
            <div key={resource.id} className={`border rounded-lg p-4 ${resource.unlocked ? 'border-gray-200 dark:border-gray-700 hover:shadow-md dark:hover:shadow-xl dark:hover:shadow-gray-900/20' : 'border-gray-100 dark:border-gray-800 opacity-60'} transition-shadow`}>
              <div className="flex items-center space-x-3 mb-3">
                <div className={`p-2 rounded-lg ${resource.unlocked ? 'bg-[#E4EFFF] dark:bg-blue-900/30' : 'bg-gray-50 dark:bg-gray-700'}`}>
                  {resource.type === 'video' && <Video className={`h-5 w-5 ${resource.unlocked ? 'text-[#323956] dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />}
                  {resource.type === 'interactive' && <Target className={`h-5 w-5 ${resource.unlocked ? 'text-[#323956] dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />}
                  {resource.type === 'assessment' && <Brain className={`h-5 w-5 ${resource.unlocked ? 'text-[#323956] dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />}
                </div>
                <div className="flex-1">
                  <h3 className={`font-medium ${resource.unlocked ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                    {resource.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{resource.duration}</p>
                </div>
              </div>

              {resource.unlocked ? (
                <button className="w-full bg-[#323956] dark:bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-[#232D3C] dark:hover:bg-blue-700 transition-colors">
                  Start
                </button>
              ) : (
                <div className="w-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 py-2 px-4 rounded-lg text-center">
                  SECURE: Complete care plan to unlock
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Referrals */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-[#E4EFFF] dark:bg-yellow-900/30 rounded-lg">
            <MapPin className="h-6 w-6 text-[#F5D05D] dark:text-yellow-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Nearby Services</h2>
        </div>

        <div className="grid gap-4">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md dark:hover:shadow-xl dark:hover:shadow-gray-900/20 transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Advanced Neurofeedback Center</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Specialized neurofeedback therapy sessions</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-2">
                  <span> 2.3 miles away</span>
                  <span> (555) 123-4567</span>
                </div>
              </div>
              <button className="flex items-center space-x-2 bg-[#F5D05D] dark:bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-[#d9b84a] dark:hover:bg-yellow-700 transition-colors">
                <Phone className="h-4 w-4" />
                <span>Contact</span>
              </button>
            </div>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md dark:hover:shadow-xl dark:hover:shadow-gray-900/20 transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Cognitive Wellness Institute</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Comprehensive cognitive assessment and training</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-2">
                  <span> 4.7 miles away</span>
                  <span> (555) 987-6543</span>
                </div>
              </div>
              <button className="flex items-center space-x-2 bg-[#F5D05D] dark:bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-[#d9b84a] dark:hover:bg-yellow-700 transition-colors">
                <Phone className="h-4 w-4" />
                <span>Contact</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const JourneySection = () => (
    <div className="space-y-6">
      {/* Progress Tracking */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-[#E4EFFF] dark:bg-blue-900/30 rounded-lg">
            <TrendingUp className="h-6 w-6 text-[#323956] dark:text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Progress Tracking</h2>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#323956] dark:border-blue-400 mx-auto"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Loading progress data...</p>
          </div>
        ) : currentStatus?.hasData ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-[#E4EFFF] dark:bg-blue-900/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#323956] dark:text-blue-400 mb-1">
                {Math.round(currentStatus.currentMetrics?.attention || 0)}
              </div>
              <div className="text-sm text-[#323956] dark:text-blue-400">Attention Score</div>
            </div>
            <div className="bg-[#CAE0FF] dark:bg-blue-900/20 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#323956] dark:text-blue-400 mb-1">
                {Math.round(currentStatus.currentMetrics?.relaxation || 0)}
              </div>
              <div className="text-sm text-[#323956] dark:text-blue-400">Relaxation Score</div>
            </div>
            <div className="bg-[#E4EFFF] dark:bg-blue-900/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#323956] dark:text-blue-400 mb-1">
                {Math.round(currentStatus.currentMetrics?.sleepQuality || 0)}
              </div>
              <div className="text-sm text-[#323956] dark:text-blue-400">Sleep Quality</div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#F5D05D] dark:text-yellow-400 mb-1">
                {Math.round(currentStatus.overallScore || 0)}
              </div>
              <div className="text-sm text-yellow-700 dark:text-yellow-400">Overall Score</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <TrendingUp className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>No progress data available yet</p>
            <p className="text-sm">Complete your first assessment to see progress tracking</p>
          </div>
        )}

        {/* Progress Trends */}
        {progressData?.trends && (
          <div className="mt-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Recent Trends</h3>
            <div className="space-y-3">
              {Object.entries(progressData.trends).map(([metric, trend]) => (
                <div key={metric} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="font-medium text-gray-900 dark:text-white capitalize">{metric.replace(/([A-Z])/g, ' $1')}</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${
                      trend.direction === 'improving' ? 'text-[#323956] dark:text-blue-400' :
                      trend.direction === 'declining' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {trend.direction === 'improving' ? '↗️' : trend.direction === 'declining' ? '↘️' : '→'}
                      {trend.direction}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({Math.abs(trend.change).toFixed(1)} pts)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Upcoming Appointments */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#E4EFFF] dark:bg-blue-900/30 rounded-lg">
              <Calendar className="h-6 w-6 text-[#323956] dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upcoming Appointments</h2>
          </div>
          <button
            onClick={() => handleBookAppointment()}
            className="flex items-center space-x-2 bg-[#323956] dark:bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-[#232D3C] dark:hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Book Follow-up</span>
          </button>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Loading appointments...</p>
            </div>
          ) : appointments.length > 0 ? (
            appointments.slice(0, 5).map((appointment) => (
              <div key={appointment.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-[#E4EFFF] dark:bg-blue-900/30 rounded-lg">
                    <Clock className="h-5 w-5 text-[#323956] dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{appointment.type}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(appointment.appointment_date).toLocaleDateString()} at {appointment.start_time}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Duration: {appointment.duration} min • ${appointment.price}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 text-sm rounded-full ${
                    appointment.status === 'scheduled'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                      : appointment.status === 'completed'
                      ? 'bg-[#E4EFFF] dark:bg-blue-900/30 text-[#323956] dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                  </span>
                  {appointment.clinics && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{appointment.clinics.name}</p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p>No upcoming appointments</p>
              <p className="text-sm">Book your first appointment to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Journey Loop */}
      <div className="bg-gradient-to-r from-[#E4EFFF] to-[#CAE0FF] dark:from-blue-900/30 dark:to-blue-900/20 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <Brain className="h-6 w-6 text-[#323956] dark:text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Your Brain Health Journey</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Continue Your Progress</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
              Regular follow-up tests help track your cognitive improvements and adjust your care plan.
            </p>
            <button className="w-full bg-[#323956] dark:bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-[#232D3C] dark:hover:bg-blue-700 transition-colors">
              Schedule Next Assessment
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Refer a Friend</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
              Share the benefits of brain health monitoring with friends and family.
            </p>
            <button className="w-full bg-[#F5D05D] dark:bg-yellow-600 text-white py-2 px-4 rounded-lg hover:bg-[#d9b84a] dark:hover:bg-yellow-700 transition-colors">
              Send Referral
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSection />;
      case 'reports':
        return <ReportsSection />;
      case 'resources':
        return <ResourcesSection />;
      case 'journey':
        return <JourneySection />;
      default:
        return <ProfileSection />;
    }
  };

  // Component to display detailed clinical report information
  const InfoField = ({ label, value }) => (
    <div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">{label}</p>
      <p className="text-sm text-gray-900 dark:text-white">{value || 'N/A'}</p>
    </div>
  );

  return (
    <>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #323956 0%, #1e2538 100%)' }}>
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* Left Section - Logo & Branding */}
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 shadow-lg">
                <img
                  src="/header logo.png"
                  alt="Brain Health Portal Logo"
                  className="h-12 w-12 object-contain"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">AIMS</h1>
                <p className="text-sm text-blue-200">Welcome back, <span className="font-medium text-white">{user?.name || 'Supervisor'}</span></p>
              </div>
            </div>

            {/* Right Section - Supervisor Info & Actions */}
            <div className="flex items-center space-x-6">
              {/* Supervisor ID Card */}
              <div className="hidden md:block bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/20">
                <div className="text-right">
                  <p className="text-xs text-blue-200 font-medium mb-0.5">Supervisor ID</p>
                  <p className="text-sm font-bold text-white tracking-wide">
                    {supervisorUid || user?.name}
                  </p>
                </div>
              </div>

              {/* User Avatar - Clickable */}
              <button
                onClick={() => setIsProfileModalOpen(true)}
                className="relative group focus:outline-none"
                title="Edit Profile Picture"
              >
                <div className="w-11 h-11 rounded-xl overflow-hidden shadow-lg transition-all duration-200 group-hover:shadow-xl group-hover:scale-105 cursor-pointer" style={{ background: profileImageUrl ? 'transparent' : 'linear-gradient(135deg, #CAE0FF 0%, #E4EFFF 100%)' }}>
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl}
                      alt="Profile"
                      className="w-full h-full object-cover pointer-events-none"
                      onError={(e) => {
                        // Fallback to icon if image fails to load
                        e.target.style.display = 'none';
                        e.target.parentElement.style.background = 'linear-gradient(135deg, #CAE0FF 0%, #E4EFFF 100%)';
                        const icon = document.createElement('div');
                        icon.className = 'w-full h-full flex items-center justify-center';
                        icon.innerHTML = '<svg class="h-6 w-6" style="color: #323956" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>';
                        e.target.parentElement.appendChild(icon);
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center pointer-events-none">
                      <User className="h-6 w-6" style={{ color: '#323956' }} />
                    </div>
                  )}
                </div>
                {/* Camera Icon Overlay */}
                <div className="absolute inset-0 bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center pointer-events-none">
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-[#323956] group-hover:scale-110 transition-transform pointer-events-none"></div>
              </button>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl border border-white/20"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 shadow-md border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <Link
                  key={tab.id}
                  to={`/dashboard/${tab.id}`}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-t-lg font-medium text-sm transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-b from-[#323956] to-[#232D3C] text-white shadow-lg -mb-px'
                      : 'text-gray-600 dark:text-gray-400 hover:text-[#323956] dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-[#CAE0FF]' : ''}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderContent()}
      </div>
    </div>

    {/* Profile Modal */}
    <ProfileModal
      isOpen={isProfileModalOpen}
      onClose={() => setIsProfileModalOpen(false)}
      onProfileUpdate={async (updatedData) => {
        console.log('DASHBOARD: onProfileUpdate called with data:', updatedData);

        // Update profile image URL when user uploads new avatar
        if (updatedData.avatar) {
          console.log('DASHBOARD: Avatar update detected, setting immediate preview');
          // Set immediate preview
          setProfileImageUrl(updatedData.avatar);

          // Wait a moment for database to be updated
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Reload from database to ensure we have the saved version
          await reloadProfileImage();

          toast.success('Profile image updated successfully!');
        } else {
          console.log('DASHBOARD: No avatar in updated data, but reloading to get latest');
          // Still reload to get any other profile updates
          await reloadProfileImage();
        }
      }}
    />
    </>
  );
};

export default SupervisorDashboard;